import { KeyboardEvent, MouseEvent, useCallback, useEffect, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useMediaQuery } from 'react-responsive';
import { KeyboardKey, Tooltip } from 'src/components';
import theme from 'src/utils/theme';
import { rtlDirectionForLocale } from 'src/utils/rtlLocales';
import { getLocalStorageEntry } from 'src/utils/webStorageUtils';
import { TARGET_LANGUAGE } from 'src/utils/language/saveLastLanguagePair';
import { ResponseState } from 'src/apps/LearningSession/hooks/learningSessionReducer';
import { getFontSize } from '../../utils';
import * as S from './TypingResponseStyles';

const msgs = defineMessages({
  hint: {
    id: 'web_hint_button',
    defaultMessage: 'Hint',
  },
  tooltip: {
    id: 'web_hint_tooltip',
    defaultMessage: 'Provides one letter of the answer',
  },
});

export interface DisplayTypingResponseProps {
  userAnswer: string;
  letters: string[];
  tentativelySelectAnswer: (answer: string) => void;
  answerTest: (answer: string) => void;
  responseState: ResponseState;
  showBigger: boolean;
  isComposing?: boolean;
  setIsComposing: (value: boolean) => void;
  hasCompositionJustEnded?: boolean;
  setCompositionJustEnded: (value: boolean) => void;
  label: string;
  correctAnswer: string;
  showCorrectAnswer: boolean;
  onVideo?: boolean;
}

export const DisplayTypingResponse = ({
  letters,
  answerTest,
  userAnswer,
  tentativelySelectAnswer,
  responseState,
  showBigger,
  isComposing,
  setIsComposing,
  hasCompositionJustEnded,
  setCompositionJustEnded,
  label,
  correctAnswer,
  showCorrectAnswer,
  onVideo = false,
}: DisplayTypingResponseProps) => {
  const intl = useIntl();
  const inputElement = useRef<HTMLInputElement>(null);
  const isMobile = useMediaQuery({ maxWidth: theme.sz.md });

  const copyText = showCorrectAnswer ? correctAnswer : null;

  useEffect(() => {
    /* istanbul ignore else: empty else path, case only here for typescript anyway */
    if (inputElement && inputElement.current) {
      inputElement.current.focus();
    }
    // This needs to depend on something specific to this test as otherwise
    // the focus is lost when there are two typing tests in a row
  }, [inputElement]);

  // User presses Enter in the input field
  const handleInputKeyUp = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (isComposing || hasCompositionJustEnded) {
        // IME composing fires keydown/keyup events
        setCompositionJustEnded(false);
        return;
      }
      // See: https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/keyCode
      // `keyCode` is deprecated but Japanese keyboards need it.
      // Removing it causes this bug: https://community.memrise.com/t/memrise-submits-answers-while-i-still-type-them/74062
      // So, prioritise it but add a fallback for if it's ever completely dropped
      /* istanbul ignore next: can't test undefined browser eventKey */
      if (event.keyCode === 13 || event?.key === 'Enter') {
        answerTest(userAnswer);
      }
    },
    [answerTest, isComposing, hasCompositionJustEnded, setCompositionJustEnded, userAnswer],
  );

  const onScreenKeyboardClick = useCallback(
    (letter: string) => {
      /* istanbul ignore else: empty else path, case only here for typescript anyway */
      if (inputElement && inputElement.current) {
        // Insert text in such a way that it goes where cursor is or replaces any selection
        inputElement.current.setRangeText(
          letter,
          inputElement.current.selectionStart || 0,
          inputElement.current.selectionEnd || 0,
          'end',
        );
        tentativelySelectAnswer(inputElement.current.value);
        inputElement.current.focus();
        setCompositionJustEnded(true);

        if (inputElement.current.value === correctAnswer) {
          setCompositionJustEnded(false);
        }
        isMobile && inputElement.current.blur();
      }
    },
    [correctAnswer, isMobile, setCompositionJustEnded, tentativelySelectAnswer],
  );

  const giveHint = () => {
    // add next correct letter
    // if user has typed incorrect letters, remove all incorrect letters and add next correct letter

    const currentAnswer = inputElement?.current?.value || '';

    /* istanbul ignore if: should not happen */
    if (!inputElement?.current) return;

    if (currentAnswer === correctAnswer) return;

    let newAnswer = currentAnswer;
    const firstWrongLetterIndex = [...currentAnswer].findIndex(
      (letter, index) => letter !== correctAnswer[index],
    );

    if (firstWrongLetterIndex >= 0) {
      newAnswer = currentAnswer.substring(0, firstWrongLetterIndex);
    }

    const currentIndex = newAnswer.length;
    const nextLetter = correctAnswer[currentIndex];

    if (nextLetter === undefined) {
      answerTest(correctAnswer);
      return;
    }
    newAnswer += nextLetter;

    tentativelySelectAnswer(newAnswer);
    inputElement.current.focus();
    setCompositionJustEnded(true);

    if (newAnswer === correctAnswer) {
      setCompositionJustEnded(false);
    }
  };

  const handleBackspace = useCallback(() => {
    /* istanbul ignore if: should not happen */
    if (!inputElement.current) return;

    const input = inputElement.current;
    const selectionStart = input.selectionStart ?? input.value.length;
    const selectionEnd = input.selectionEnd ?? input.value.length;

    if (selectionStart === 0 && selectionEnd === 0) return;

    if (selectionStart !== selectionEnd) {
      input.setRangeText('', selectionStart, selectionEnd, 'end');
    } else {
      const beforeCursor = input.value.slice(0, selectionStart);
      const codePoints = [...beforeCursor];
      if (codePoints.length === 0) return;
      const deleteFrom = codePoints.slice(0, -1).join('').length;
      input.setRangeText('', deleteFrom, selectionStart, 'end');
    }

    tentativelySelectAnswer(input.value);
    input.focus();
    setCompositionJustEnded(true);
    isMobile && input.blur();
  }, [isMobile, setCompositionJustEnded, tentativelySelectAnswer]);

  const preventButtonFocusSteal = (event: MouseEvent) => {
    event.preventDefault();
  };

  const isBackspaceDisabled =
    !['Unanswered', 'Selected'].includes(responseState) || userAnswer.length === 0;

  const savedTargetLanguage = getLocalStorageEntry(TARGET_LANGUAGE);
  const targetLanguageDir = savedTargetLanguage
    ? rtlDirectionForLocale(savedTargetLanguage)
    : 'ltr';

  return (
    <S.Container $onVideo={onVideo} data-testid="typing-response">
      <S.Label dir="auto" id="typing-response-label" $onVideo={onVideo}>
        {label}
      </S.Label>
      <S.InputContainer $onVideo={onVideo}>
        <S.InputWrapper
          dir={targetLanguageDir}
          $placeholderText={copyText || ''}
          $fontSize={getFontSize(showBigger, theme)}
          data-testid="typing-response-input-wrapper"
        >
          <S.Input
            dir="auto"
            $isRtlTarget={targetLanguageDir === 'rtl'}
            enterKeyHint="send"
            type="text"
            value={userAnswer}
            data-testid="typing-response-input"
            spellCheck="false"
            autoCapitalize="off"
            autoComplete="off"
            autoCorrect="off"
            aria-labelledby="typing-response-label"
            onChange={(e: { target: { value: string } }) => tentativelySelectAnswer(e.target.value)}
            onCompositionStart={() => setIsComposing(true)}
            onCompositionEnd={() => {
              setIsComposing(false);
              /* istanbul ignore else: if eliminates undefined for TS */
              // This tentative selection is necessary for diacritics: https://community.memrise.com/t/answer-box-wont-accept-diacritics/75302
              if (inputElement.current) {
                tentativelySelectAnswer(inputElement.current.value);
              }
              // See: https://www.stum.de/2016/06/24/handling-ime-events-in-javascript/
              // Some browsers (IE, Firefox, Safari) send a keyup event after compositionend, some (Chrome, Edge) don't.
              // This is to swallow the next keyup event, unless a keydown event happens first.
              setCompositionJustEnded(true);
            }}
            $responseState={responseState}
            ref={inputElement}
            onKeyUp={handleInputKeyUp}
            disabled={!['Unanswered', 'Selected'].includes(responseState)}
            $fontSize={getFontSize(showBigger, theme)}
            placeholder={
              ' ' /* placeholder allows usage of :placeholder-shown in css, to enforce correct cursor placement in empty input */
            }
          />
        </S.InputWrapper>
      </S.InputContainer>
      <S.BottomContainer $onVideo={onVideo}>
        <S.KeyContainer
          dir={targetLanguageDir}
          $onVideo={onVideo}
          data-testid="typing-response-keys"
        >
          {letters.map((letter, i) => (
            <KeyboardKey
              key={`${letter}-${i}`} // eslint-disable-line react/no-array-index-key
              data-testid={`typing-response-${letter}-${i}`}
              disabled={false}
              onClick={onScreenKeyboardClick}
              letter={letter}
              showBigger={showBigger}
              onVideo={onVideo}
            />
          ))}
        </S.KeyContainer>
        <S.HintContainer $onVideo={onVideo} data-testid="typing-response-hints">
          <Tooltip
            placement="bottom"
            tooltip={intl.formatMessage(msgs.tooltip)}
            disabled={isMobile}
          >
            <S.HintButton
              $showBigger={showBigger}
              $onVideo={onVideo}
              onClick={giveHint}
              onMouseDown={preventButtonFocusSteal}
            >
              <S.HintIcon name="hint" />
              {intl.formatMessage(msgs.hint)}
            </S.HintButton>
          </Tooltip>
          <S.BackspaceButton
            type="button"
            $showBigger={showBigger}
            $onVideo={onVideo}
            onClick={handleBackspace}
            onMouseDown={preventButtonFocusSteal}
            disabled={isBackspaceDisabled}
            data-testid="typing-response-backspace"
            aria-label="Backspace"
          >
            <S.BackspaceIcon name="backspace" $showBigger={showBigger} />
          </S.BackspaceButton>
        </S.HintContainer>
      </S.BottomContainer>
    </S.Container>
  );
};
